import {
  action, makeObservable, observable,
} from "mobx";
import { IChase, ChaseState } from "../types/IChase";
import { ILead } from "../types/ILead";
import { Meta } from "../types/Meta";
import { RootStore } from "./RootStore";
import * as arrayUtils from "../utils/arrays";
import * as leadsService from "../requests/leads";
import * as chaseService from "../requests/chases";
import * as campaignChasesService from "../requests/campaigns/chases";
import { IChaseIndexFilters } from "../requests/campaigns/chases";
import * as leadChaseService from "../requests/leads/chases";
import {
  IChaseActivity, ChaseActivityType, IChaseReminderActivity, IChasePhoneCallActivity,
} from "../types/IChaseActivity";
import { IReminder } from "../types/IReminder";
import { IComment } from "../types/IComment";

const CHASE_STATES_IN_FUNNEL: ChaseState[] = [
  ChaseState.ACTIVE,
  ChaseState.MARKED_QUALIFIED,
  ChaseState.QUALIFIED,
  ChaseState.MARKED_HOT,
  ChaseState.HOT,
  ChaseState.CLOSED,
];

interface ILeadsMeta extends Omit<Meta, "count"> {
  activePipelineAmount: number;
  activePipelineCount: number;
  activePipelineTotalAmount: number;
  activePipelineTotalCount: number;
  totalPipelineAmount: number;
  totalPipelineCount: number;
  totalPipelineTotalAmount: number;
  totalPipelineTotalCount: number;
}

export interface IHopperMeta {
  campaignId: number;
  flagged?: ILeadsMeta;
  active?: Meta;
  marked_qualified?: Meta;
  qualified?: Meta;
  marked_hot?: Meta;
  hot?: Meta;
  closed?: Meta;
}

export class ChasesStore {
  rootStore;

  @observable chases: IChase[] = [];

  @observable chaseActivity: IChaseActivity[] = [];

  @observable flaggedLeads: ILead[] = [];

  @observable hopperMetas: IHopperMeta[] = [];

  @observable isLoading = false;

  @observable filters: IChaseIndexFilters = {};

  constructor(rootStore: RootStore) {
    makeObservable(this);

    this.rootStore = rootStore;
  }

  @action
  cacheChases(chases: IChase[]) {
    const allChases = [...this.chases, ...chases];

    const allChasesUnique = arrayUtils.uniqueOnId(allChases, "id");

    this.setChases(allChasesUnique);
  }

  @action
  setFilters(newFilters: IChaseIndexFilters) {
    this.filters = newFilters;
  }

  @action
  setChases(chases: IChase[]) {
    this.chases = chases;
  }

  @action
  setChaseActivity(chaseActivity: IChaseActivity[]) {
    this.chaseActivity = chaseActivity;
  }

  @action
  setFlaggedLeads(flaggedLeads: ILead[]) {
    this.flaggedLeads = flaggedLeads;
  }

  @action
  cacheFlaggedLeads(flaggedLeads: ILead[]) {
    const allFlaggedLeads = [...this.flaggedLeads, ...flaggedLeads];

    const allFlaggedLeadsUnique = arrayUtils.uniqueOnId(allFlaggedLeads, "id");

    this.setFlaggedLeads(allFlaggedLeadsUnique);
  }

  @action
  setHopperMetas(hopperMetas: IHopperMeta[]) {
    this.hopperMetas = hopperMetas;
  }

  @action
  cacheHopperMeta(hopperMeta: IHopperMeta) {
    const allMetas = [hopperMeta, ...this.hopperMetas];

    const allMetasUnique = arrayUtils.uniqueOnId(allMetas, "campaignId");

    this.setHopperMetas(allMetasUnique);
  }

  @action
  updateHopperStateMeta(campaignId: number, aasmState: ChaseState | "flagged", meta: Meta | ILeadsMeta) {
    const hopperMeta = this.getHopperMetaForCampaignId(campaignId);

    if (!hopperMeta) {
      return;
    }

    if (aasmState === "flagged") {
      hopperMeta[aasmState] = meta as ILeadsMeta;
    } else {
      hopperMeta[aasmState] = meta as Meta;
    }

    this.cacheHopperMeta(hopperMeta);
  }

  getChasesForLeadId(leadId: string) {
    return this.chases.filter((chase: IChase) => chase.attributes.leadId.toString() === leadId);
  }

  getChaseForLeadIdAndUserId(leadId: string, userId: string) {
    return this.getChasesForLeadId(leadId).find(
      (chase: IChase) => chase.attributes.userId.toString() === userId,
    );
  }

  @action
  closeAllChasesForLeadId(leadId: string) {
    const chases = this.getChasesForLeadId(leadId);
    const chaseIds = chases.map((chase: IChase) => chase.id);

    const updatedChases = this.chases.map((chase: IChase) => {
      if (chaseIds.includes(chase.id)) {
        return {
          ...chase,
          attributes: {
            ...chase.attributes,
            aasmState: ChaseState.CLOSED,
          },
        };
      }
      return chase;
    });

    this.setChases(updatedChases);
  }

  @action
  cacheChaseActivityForLeadIdAndUserId(
    leadId: string,
    userId: string,
    activityType: ChaseActivityType.PHONE_CALL | ChaseActivityType.REMINDER,
    createdAt: string,
  ) {
    const chase = this.getChaseForLeadIdAndUserId(leadId, userId);
    if (!chase) return;

    const activity = {
      id: `${activityType}-${createdAt}`,
      attributes: {
        createdAt,
        userId,
        type: activityType,
        item: activityType === ChaseActivityType.REMINDER ? {} as IReminder : {} as IComment,
      },
    } as IChaseReminderActivity | IChasePhoneCallActivity;

    chase.attributes.activities = [activity, ...chase.attributes.activities];
    this.cacheChases([chase]);
  }

  getChaseById(chaseId: string): IChase | undefined {
    return this.chases.find((chase: IChase) => chase.id === chaseId);
  }

  @action
    getChaseActivity = async (chaseId: string) => {
      const result = await chaseService.getChaseActivity(chaseId);

      const sorted = result.sort((a, b) => new Date(b.attributes.createdAt).getTime()
    - new Date(a.attributes.createdAt).getTime());

      this.setChaseActivity(sorted);

      return sorted;
    };

  @action
  async getChasesForLeadIdAsync(leadId: string) {
    const result = await leadChaseService.getChases(leadId);
    this.cacheChases(result);
  }

  @action
  async createChase(leadId: string, userId: string) {
    const result = await leadChaseService.postChase(leadId, userId);
    this.setChases([...this.chases, result]);
  }

  @action
  async updateChase(chaseId: string, payload: chaseService.IChasePayload) {
    const result = await chaseService.putChase(chaseId, payload);

    const chaseIndex = this.chases.findIndex((chase) => chase.id === chaseId);
    this.setChases([
      ...this.chases.slice(0, chaseIndex),
      result,
      ...this.chases.slice(chaseIndex + 1),
    ]);
  }

  @action
  async getFlaggedLeadsForCampaignIdAsync(page = 1) {
    const campaignId = this.rootStore.context.campaign.id;

    const { data, meta } = await leadsService.getLeads({
      flagged: true,
      page,
    });

    this.cacheFlaggedLeads(data);
    this.updateHopperStateMeta(campaignId, "flagged", meta);
  }

  @action
  async getNextPageForCampaignFlaggedLeadsAsync() {
    const campaignId = this.rootStore.context.campaign.id;
    const flaggedLeadsMeta = this.getHopperMetaForCampaignId(campaignId);

    if (!flaggedLeadsMeta || !flaggedLeadsMeta.flagged?.page.more) {
      return;
    }

    const page = flaggedLeadsMeta.flagged.page.next as number;
    await this.getFlaggedLeadsForCampaignIdAsync(page);
  }

  @action
  async getChasesForCampaignIdAsync(campaignId: number, payload: IChaseIndexFilters) {
    const result = await campaignChasesService.getChases(campaignId, payload);

    this.cacheChases(result.data);
    this.updateHopperStateMeta(campaignId, payload.aasmState as ChaseState, result.meta);
  }

  @action
  async getHopperForCampaignIdAsync(campaignId: number) {
    this.cacheHopperMeta({ campaignId });

    const flaggedLeadsColumn = this.getFlaggedLeadsForCampaignIdAsync();

    const chaseStatesColumns = CHASE_STATES_IN_FUNNEL.map(
      async (aasmState: ChaseState) => {
        await this.getChasesForCampaignIdAsync(campaignId, {
          aasmState,
          ...this.filters,
        });
      },
    );

    await Promise.all([flaggedLeadsColumn, ...chaseStatesColumns]);
  }

  @action
  async filterAndGetHopperForCampaignIdAsync(campaignId: number, newFilters: IChaseIndexFilters) {
    this.setFilters({
      ...this.filters,
      ...newFilters,
    });

    // Clear the meta
    this.cacheHopperMeta({ campaignId });

    await this.getHopperForCampaignIdAsync(campaignId);
  }

  @action
  async getNextPageForCampaignIdStateAsync(
    campaignId: number,
    aasmState: ChaseState,
  ) {
    const hopperMeta = this.getHopperMetaForCampaignIdState(campaignId, aasmState);

    if (!hopperMeta || !hopperMeta.page.more) {
      return;
    }

    const page: number = hopperMeta.page.next as number;

    await this.getChasesForCampaignIdAsync(campaignId, {
      aasmState,
      page,
    });
  }

  @action
  getHopperMetaForCampaignId(campaignId: number): IHopperMeta | undefined {
    return this.hopperMetas.find((meta: IHopperMeta) => meta.campaignId === campaignId);
  }

  @action
  getHopperMetaForCampaignIdState(campaignId: number, aasmState: ChaseState): Meta | undefined {
    return this.getHopperMetaForCampaignId(campaignId)?.[aasmState];
  }

  @action
  async deleteChase(chaseId: string) {
    await chaseService.deleteChase(chaseId);

    const chase = this.chases.find((storedChase) => storedChase.id === chaseId);

    if (chase) {
      const { lead } = chase.attributes;

      if (lead) {
        const { campaignId } = lead.attributes;
        const { aasmState } = chase.attributes;
        const meta = this.getHopperMetaForCampaignIdState(campaignId, aasmState);

        if (meta) {
          meta.count -= 1;

          this.updateHopperStateMeta(campaignId, aasmState, meta);
        }
      }
    }

    this.setChases(this.chases.filter((storedChase) => storedChase.id !== chaseId));
  }
}

export default ChasesStore;
